import React, { useEffect, useState, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const TABLE_NAME = 'rd_cco';

// Campi per cui vogliamo caricare i valori distinti già presenti in tabella
// (usati come suggerimenti in un <datalist>, ma il campo resta sempre di testo libero)
const SELECT_FIELDS = [
  'tipointervento',
  'n_macchina',
  'linea',
  'colata',
  'posizione',
  'stato',
  'stato_rid',
];

const getTipoConfig = (tipo) => {
  const t = tipo?.toUpperCase() || '';
  if (t === 'MONTATO') return { color: 'text-emerald-500 font-black', dot: 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,.5)]', bg: 'bg-emerald-500/10 border-emerald-500/30', label: 'MONTATO' };
  if (t === 'SMONTATO') return { color: 'text-red-500 font-black', dot: 'bg-red-500 shadow-[0_0_8px_rgba(239,68,68,.5)]', bg: 'bg-red-500/10 border-red-500/30', label: 'SMONTATO' };
  return { color: 'text-slate-400 font-bold', dot: 'bg-slate-400', bg: 'bg-slate-400/10 border-slate-400/30', label: tipo || 'N/D' };
};

const getStatoConfig = (stato) => {
  const s = stato?.toUpperCase() || '';
  if (s === 'PRONTO') return { color: 'text-emerald-500 font-black', dot: 'bg-emerald-500 animate-pulse shadow-[0_0_8px_rgba(16,185,129,.5)]', bg: 'bg-emerald-500/10 border-emerald-500/30', label: 'PRONTO' };
  if (s.includes('RIPARAZIONE')) return { color: 'text-orange-500 font-black', dot: 'bg-orange-500', bg: 'bg-orange-500/10 border-orange-500/30', label: 'IN RIPARAZIONE' };
  return { color: 'text-slate-400 font-bold', dot: 'bg-slate-400', bg: 'bg-slate-400/10 border-slate-400/30', label: stato || 'N/D' };
};

const fmtDatetime = (val) => {
  if (!val) return '---';
  try { return new Date(val).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return val; }
};

const fmtDate = (val) => {
  if (!val) return '---';
  try { return new Date(val).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }); } catch { return val; }
};

const toDatetimeLocal = (val) => {
  if (!val) return '';
  try {
    const d = new Date(val);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
};

const safeString = (value) => String(value ?? '');

const uniqueValues = (values) => [...new Set(values.map((value) => safeString(value).trim()).filter(Boolean))]
  .sort((a, b) => a.localeCompare(b, 'it', { numeric: true, sensitivity: 'base' }));

// ─── Card di sezione (glassmorphism, stesso stile di NewInsertRaddrizzatrice) ──
const FormCard = ({ titolo, icon, descrizione, gridClassName = 'grid-cols-1 sm:grid-cols-2', children }) => (
  <section className="rounded-3xl border border-border/40 bg-card/40 backdrop-blur-md shadow-xl shadow-black/5 p-5 sm:p-6 flex flex-col gap-5">
    <header className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary shrink-0">
        <Icon name={icon} size={16} />
      </div>
      <div className="flex flex-col">
        <h3 className="text-[11px] font-black uppercase tracking-[.25em] text-primary/80">{titolo}</h3>
        {descrizione && <p className="text-[10px] font-bold text-muted-foreground/50 mt-0.5">{descrizione}</p>}
      </div>
    </header>
    <div className={`grid ${gridClassName} gap-5`}>
      {children}
    </div>
  </section>
);

// ─── Campo di testo semplice ────────────────────────────────────────────────────
const Campo = ({ label, value, mono = false, isEditing, onChange, type = 'text', name, disabled = false, placeholder = '', icon }) => {
  const displayValue = value ?? '';
  return (
    <div className="flex flex-col gap-1.5 min-w-0 group">
      <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none truncate transition-colors group-focus-within:text-primary">{label}</span>
      {isEditing && !disabled ? (
        <div className="relative">
          {icon && <Icon name={icon} size={14} className="absolute left-3.5 top-3.5 text-primary/40 pointer-events-none transition-colors group-focus-within:text-primary" />}
          {type === 'textarea' ? (
            <textarea
              name={name}
              value={displayValue}
              onChange={onChange}
              placeholder={placeholder}
              rows={3}
              className={`w-full rounded-2xl px-4 py-3 text-sm font-medium outline-none transition-all resize-none backdrop-blur-md bg-muted/30 border border-border/40 focus:border-primary/50 focus:bg-background/80 focus:ring-4 focus:ring-primary/10 hover:border-border/70 ${icon ? 'pl-10' : ''}`}
            />
          ) : (
            <input
              type={type}
              name={name}
              value={displayValue}
              onChange={onChange}
              disabled={disabled}
              placeholder={placeholder}
              className={`w-full rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all backdrop-blur-md bg-muted/30 border border-border/40 focus:border-primary/50 focus:bg-background/80 focus:ring-4 focus:ring-primary/10 hover:border-border/70 ${icon ? 'pl-10' : ''}`}
            />
          )}
        </div>
      ) : (
        <span className={`text-[14px] font-bold text-foreground/85 break-words leading-snug ${mono ? 'font-mono tracking-tight text-[13px]' : ''}`}>{displayValue || '---'}</span>
      )}
    </div>
  );
};

// Campo di testo libero con suggerimenti (datalist) presi dai valori già esistenti in tabella.
// L'utente può sempre scrivere un valore nuovo, non è mai bloccato su una lista fissa.
const ComboCampo = ({ label, value, isEditing, onChange, name, placeholder = '', options = [], icon }) => {
  const displayValue = value ?? '';
  const listId = `dettagli-rd-${name}`;
  return (
    <div className="flex flex-col gap-1.5 min-w-0 group">
      <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none truncate transition-colors group-focus-within:text-primary">{label}</span>
      {isEditing ? (
        <div className="relative">
          {icon && <Icon name={icon} size={14} className="absolute left-3.5 top-3.5 text-primary/40 pointer-events-none transition-colors group-focus-within:text-primary" />}
          <input
            list={listId}
            name={name}
            value={displayValue}
            onChange={onChange}
            placeholder={placeholder}
            autoComplete="off"
            className={`w-full rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all backdrop-blur-md bg-muted/30 border border-border/40 focus:border-primary/50 focus:bg-background/80 focus:ring-4 focus:ring-primary/10 hover:border-border/70 ${icon ? 'pl-10' : ''}`}
          />
          {options.length > 0 && (
            <datalist id={listId}>
              {options.map((opt) => <option key={opt} value={opt} />)}
            </datalist>
          )}
        </div>
      ) : (
        <span className="text-[14px] font-bold text-foreground/85 break-words leading-snug">{displayValue || '---'}</span>
      )}
    </div>
  );
};

// ─── Switch premium per Causa Freno (stesso stile di NewInsertRaddrizzatrice) ──
const CausaFrenoField = ({ value, isEditing, onChange }) => {
  if (!isEditing) {
    return (
      <div className="flex flex-col gap-1.5 min-w-0">
        <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 select-none truncate">Causa Freno</span>
        <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border w-fit ${value ? 'bg-red-500/10 border-red-500/30 text-red-500' : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600'}`}>
          <Icon name={value ? 'AlertTriangle' : 'CheckCircle2'} size={14} />
          <span className="text-sm font-black uppercase tracking-widest">{value ? 'SI' : 'NO'}</span>
        </div>
      </div>
    );
  }

  const toggle = () => onChange({ target: { name: 'causa_freno', type: 'checkbox', checked: !value, value: !value } });

  return (
    <div className="flex flex-col gap-1.5 w-full group">
      <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/60 group-focus-within:text-primary">Causa Freno</span>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(); } }}
        className={`relative flex items-center justify-between w-full rounded-2xl px-4 py-3.5 border-2 backdrop-blur-md transition-all duration-300 focus:outline-none focus:ring-4 ${
          value
            ? 'bg-red-500/10 border-red-500/40 text-red-500 focus:ring-red-500/20 shadow-[0_0_20px_-4px_rgba(239,68,68,0.35)]'
            : 'bg-emerald-500/10 border-emerald-500/40 text-emerald-600 focus:ring-emerald-500/20 shadow-[0_0_20px_-4px_rgba(16,185,129,0.25)]'
        }`}
      >
        <span className="flex items-center gap-2.5">
          <Icon name={value ? 'AlertTriangle' : 'CheckCircle2'} size={17} className={value ? 'animate-pulse' : ''} />
          <span className="text-sm font-black tracking-tight">
            {value ? 'SI — Causa freno presente' : 'NO — Nessuna causa freno'}
          </span>
        </span>
        <span className={`relative w-11 h-6 rounded-full transition-colors duration-300 shrink-0 ${value ? 'bg-red-500' : 'bg-emerald-500'}`}>
          <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md transition-all duration-300 ${value ? 'left-5' : 'left-0.5'}`} />
        </span>
      </button>
      <span className="px-1 text-[9px] font-bold text-muted-foreground/40">
        Nel database verrà salvato come valore booleano: {value ? 'true' : 'false'}.
      </span>
    </div>
  );
};

const Detagli_Intervento = ({ item, onClose, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({ ...item });
  const [loading, setLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(false);
  const [notification, setNotification] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [options, setOptions] = useState({});
  const [optionsLoading, setOptionsLoading] = useState(false);
  const utenteRef = useRef(null);

  if (!item) return null;

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && !showDeleteConfirm) onClose(); };
    window.addEventListener('keydown', handler);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', handler); document.body.style.overflow = 'unset'; };
  }, [onClose, showDeleteConfirm]);

  useEffect(() => {
    if (!notification || notification.type !== 'success') return;
    const timeout = setTimeout(() => setNotification(null), 4000);
    return () => clearTimeout(timeout);
  }, [notification]);

  // ── Carica i valori distinti già presenti in rd_cco per i suggerimenti (datalist) ──
  const fetchOptions = useCallback(async () => {
    setOptionsLoading(true);
    try {
      const result = {};
      await Promise.all(SELECT_FIELDS.map(async (field) => {
        const { data, error } = await supabase.from(TABLE_NAME).select(field).not(field, 'is', null);
        if (error) throw error;
        result[field] = uniqueValues((data || []).map((row) => row[field]));
      }));
      setOptions(result);
    } catch (err) {
      console.error('Errore caricamento opzioni rd_cco:', err.message);
    } finally {
      setOptionsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOptions();
  }, [fetchOptions]);

  // ── PDF compatto — tutto su una singola pagina A4, focus su Linea ───────────
  const generatePDF = () => {
    try {
      const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

      // Header
      doc.setFontSize(11); doc.setFont('helvetica', 'bold'); doc.setTextColor(37, 99, 235);
      doc.text('OrizonDesk', 14, 14);
      doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(140);
      doc.text('Scheda Intervento Raddrizzatrice', 14, 19);

      doc.setFontSize(20); doc.setFont('helvetica', 'bold'); doc.setTextColor(15, 23, 42);
      doc.text(`LINEA: ${(formData.linea || 'N/D').toUpperCase()}`, 14, 30);

      doc.setDrawColor(226, 232, 240);
      doc.line(14, 34, 196, 34);

      // Tabella a 2 colonne di coppie (etichetta/valore) per stare su una sola pagina
      const pairs = [
        ['STATO ATTUALE', (formData.stato || '---').toUpperCase(), 'TIPO INTERVENTO', (formData.tipointervento || '---').toUpperCase()],
        ['N° MACCHINA', formData.n_macchina || '---', 'LINEA', formData.linea || '---'],
        ['COLATA', formData.colata || '---', 'POSIZIONE', formData.posizione || '---'],
        ['CAUSA FRENO', formData.causa_freno ? 'SI' : 'NO', 'STATO RID', formData.stato_rid || '---'],
        ['DATA INTERVENTO', fmtDatetime(formData.data_intervento), 'DATA RIENTRO', fmtDate(formData.data_rientro)],
        ['S/N RIENTRATO', formData.snrientrato || '---', 'S/N SMONTATO', formData.snsmontato || '---'],
        ['S/N MONTATO', formData.snmontato || '---', '', ''],
      ];

      autoTable(doc, {
        startY: 39,
        body: pairs,
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 3.2, overflow: 'linebreak', valign: 'middle' },
        columnStyles: {
          0: { cellWidth: 36, fontStyle: 'bold', textColor: [100, 100, 100], fillColor: [248, 250, 252] },
          1: { cellWidth: 56 },
          2: { cellWidth: 36, fontStyle: 'bold', textColor: [100, 100, 100], fillColor: [248, 250, 252] },
          3: { cellWidth: 54 },
        },
        didParseCell: (data) => {
          if (data.column.index === 1 || data.column.index === 3) {
            const text = String(data.cell.raw || '').toUpperCase();
            if (['SMONTATO', 'NO'].includes(text)) data.cell.styles.textColor = [220, 38, 38];
            if (['MONTATO', 'SI', 'PRONTO'].includes(text)) data.cell.styles.textColor = [16, 185, 129];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });

      // Note — blocco a piena larghezza sotto la tabella
      let noteY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(100, 100, 100);
      doc.text('NOTE INTERVENTO', 14, noteY);
      noteY += 5;
      doc.setFontSize(9.5); doc.setFont('helvetica', 'normal'); doc.setTextColor(30, 30, 30);
      const noteLines = doc.splitTextToSize(formData.note || '---', 182);
      doc.text(noteLines, 14, noteY);

      // Footer
      const pageSize = doc.internal.pageSize;
      const pageW = pageSize.width || pageSize.getWidth();
      const pageH = pageSize.height || pageSize.getHeight();
      doc.setFontSize(8); doc.setTextColor(150); doc.setFont('helvetica', 'normal');
      doc.text(`Stampato il: ${new Date().toLocaleString('it-IT')}`, 14, pageH - 10);
      doc.text(`Pagina ${doc.internal.getNumberOfPages()}`, pageW / 2, pageH - 10, { align: 'center' });
      doc.text('https://desk.orizon-aix.com', pageW - 14, pageH - 10, { align: 'right' });

      doc.save(`Scheda_Intervento_Raddrizzatrice_${formData.linea || formData.n_macchina || 'ND'}.pdf`);
    } catch (err) { console.error('PDF Error:', err); alert('Errore generazione PDF'); }
  };

  const handleInputChange = (e) => { const { name, value, type, checked } = e.target; setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value })); };

  const handleEditClick = async () => {
    if (isEditing) { handleSave(); return; }
    setIsCheckingAuth(true);
    try {
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) { setNotification({ type: 'error', message: 'Devi essere loggato per modificare!' }); return; }
      const { data: profil, error: dbError } = await supabase.from('profili').select('nome, cognome, ruolo').eq('id', user.id).single();
      if (dbError || !profil) { setNotification({ type: 'error', message: 'Errore nel recupero del profilo!' }); return; }
      if (profil.ruolo === 'cco-admin' || profil.ruolo === 'Owner') { utenteRef.current = profil; fetchOptions(); setIsEditing(true); } else setNotification({ type: 'error', message: `${profil.nome || 'Utente'}, non sei autorizzato a modificare gli interventi!` });
    } catch { setNotification({ type: 'error', message: 'Errore imprevisto!' }); } finally { setIsCheckingAuth(false); }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const payload = { ...formData };
      if (payload.data_intervento && payload.data_intervento.includes('T') && !payload.data_intervento.includes('Z')) payload.data_intervento = new Date(payload.data_intervento).toISOString();
      if (!payload.data_rientro) payload.data_rientro = null;
      const { id, created_at, ...updatePayload } = payload;
      const { error } = await supabase.from(TABLE_NAME).update(updatePayload).eq('id', item.id);
      if (error) throw error;
      setNotification({ type: 'success', message: 'Intervento aggiornato con successo!' });
      if (onUpdate) onUpdate({ ...item, ...updatePayload });
      setIsEditing(false);
    } catch (err) { setNotification({ type: 'error', message: 'Errore nel salvataggio: ' + err.message }); } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    setLoading(true); setShowDeleteConfirm(false);
    try {
      const { error } = await supabase.from(TABLE_NAME).delete().eq('id', item.id);
      if (error) throw error;
      setNotification({ type: 'success', message: 'Intervento eliminato con successo!' });
      setTimeout(() => onClose(), 2500);
    } catch (err) { setNotification({ type: 'error', message: "Errore durante l'eliminazione: " + err.message }); setLoading(false); }
  };

  const handleAnnulla = () => { setIsEditing(false); setFormData({ ...item }); };
  const tipoCfg = getTipoConfig(formData.tipointervento);
  const statoCfg = getStatoConfig(formData.stato);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-6 overflow-hidden">
      <div className="absolute inset-0 bg-background/80 backdrop-blur-xl animate-in fade-in duration-300" onClick={!showDeleteConfirm ? onClose : undefined} />

      {notification && (
        <div role="status" aria-live="polite" className={`fixed top-8 left-1/2 -translate-x-1/2 z-[150] px-5 py-3.5 rounded-2xl shadow-2xl flex items-center gap-3 animate-in slide-in-from-top-4 duration-300 max-w-[90vw] ${notification.type === 'success' ? 'bg-emerald-500 text-white' : 'bg-red-500 text-white'}`}>
          <Icon name={notification.type === 'success' ? 'CheckCircle' : 'AlertCircle'} size={18} />
          <span className="text-sm font-black uppercase tracking-wider">{notification.message}</span>
          <button onClick={() => setNotification(null)} aria-label="Chiudi notifica" className="ml-2 shrink-0 w-5 h-5 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center"><Icon name="X" size={11} /></button>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4" role="alertdialog" aria-modal="true" aria-labelledby="delete-confirm-title">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative w-full max-w-sm bg-card/90 backdrop-blur-xl border border-border/60 rounded-3xl shadow-2xl overflow-hidden">
            <div className="h-1 w-full bg-gradient-to-r from-red-500 via-red-400 to-transparent" />
            <div className="p-7 flex flex-col gap-5">
              <div className="flex flex-col gap-1.5">
                <span id="delete-confirm-title" className="text-base font-black uppercase tracking-tight">Eliminare questo intervento?</span>
                <span className="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest">Linea {item.linea || 'N/D'} — {fmtDatetime(item.data_intervento)}</span>
                <span className="text-[11px] text-red-500/80 font-bold">L'operazione non è reversibile.</span>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setShowDeleteConfirm(false)} className="flex-1 px-4 py-2.5 rounded-2xl bg-muted border border-border/40 text-foreground text-[11px] font-black uppercase tracking-widest">Annulla</button>
                <button onClick={handleDelete} className="flex-1 px-4 py-2.5 rounded-2xl bg-red-500 text-white border border-red-600 text-[11px] font-black uppercase tracking-widest">Elimina</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="detagli-title"
        className="relative w-full h-full md:h-auto md:max-h-[90vh] md:max-w-2xl bg-card/80 backdrop-blur-xl border-t md:border border-border/50 rounded-t-[2.5rem] md:rounded-[2.5rem] shadow-2xl flex flex-col animate-in slide-in-from-bottom-10 md:zoom-in-95 duration-300"
      >
        <div className="absolute -top-20 -right-20 w-56 h-56 bg-primary/10 blur-[70px] rounded-full pointer-events-none" />
        <div className="md:hidden flex justify-center pt-3 pb-1 relative z-10"><div className="w-12 h-1.5 rounded-full bg-muted/50" /></div>

        {/* Header — focus su Linea */}
        <div className="shrink-0 px-6 md:px-8 pt-5 pb-6 border-b border-border/40 relative z-10">
          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-2 min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl border ${tipoCfg.bg}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${tipoCfg.dot}`} />
                  <span className={`text-[10px] font-black uppercase tracking-widest ${tipoCfg.color}`}>{tipoCfg.label}</span>
                </div>
                <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl border ${statoCfg.bg}`}>
                  <span className={`w-1.5 h-1.5 rounded-full ${statoCfg.dot}`} />
                  <span className={`text-[10px] font-black uppercase tracking-widest ${statoCfg.color}`}>{statoCfg.label}</span>
                </div>
              </div>

              {isEditing ? (
                <input
                  name="linea"
                  value={formData.linea ?? ''}
                  onChange={handleInputChange}
                  className="bg-transparent border-b border-primary/30 font-black uppercase tracking-tighter text-foreground leading-[1.1] outline-none text-2xl w-full"
                  placeholder="Linea..."
                />
              ) : (
                <h3 id="detagli-title" className="font-black uppercase tracking-tighter text-foreground leading-[1.1] break-words" style={{ fontSize: 'clamp(1.4rem, 5vw, 2rem)' }}>
                  {formData.linea || 'N/D'}
                </h3>
              )}

              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[10px] font-black uppercase tracking-[.3em] text-muted-foreground/40">Scheda Intervento Raddrizzatrice</span>
                <span className="text-muted-foreground/20">•</span>
                <span className="text-[10px] font-bold text-muted-foreground/40">N° Macchina: {formData.n_macchina || 'N/D'}</span>
              </div>
            </div>

            <div className="flex gap-1.5 shrink-0">
              {!isEditing && (
                <button onClick={generatePDF} title="Scarica PDF" aria-label="Scarica PDF" className="w-9 h-9 rounded-xl bg-blue-500/10 text-blue-500 hover:bg-blue-500 hover:text-white border border-blue-500/20 flex items-center justify-center">
                  <Icon name="FileDown" size={16} />
                </button>
              )}
              <button
                onClick={handleEditClick}
                disabled={loading || isCheckingAuth}
                aria-label={isEditing ? 'Salva modifiche' : 'Modifica'}
                className={`w-9 h-9 rounded-xl flex items-center justify-center border ${isEditing ? 'bg-emerald-500 text-white border-emerald-600' : 'bg-muted/50 text-muted-foreground hover:text-primary border-border/40'}`}
              >
                <Icon name={(loading || isCheckingAuth) ? 'Loader2' : isEditing ? 'Check' : 'Edit3'} size={16} className={(loading || isCheckingAuth) ? 'animate-spin' : ''} />
              </button>
              {!isEditing && (
                <button onClick={onClose} aria-label="Chiudi" className="w-9 h-9 rounded-xl bg-muted/50 hover:bg-red-500/10 text-muted-foreground hover:text-red-500 flex items-center justify-center">
                  <Icon name="X" size={16} />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 md:p-7 flex flex-col gap-6 relative z-10" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(128,128,128,0.15) transparent' }}>

          <FormCard titolo="Tipo & Stato" icon="Activity" gridClassName="grid-cols-1 md:grid-cols-2">
            <ComboCampo label="Tipo Intervento" name="tipointervento" value={formData.tipointervento} isEditing={isEditing} onChange={handleInputChange} placeholder="Es. MONTATO, SMONTATO..." options={options.tipointervento || []} icon="Wrench" />
            <ComboCampo label="Stato" name="stato" value={formData.stato} isEditing={isEditing} onChange={handleInputChange} placeholder="Es. PRONTO, IN RIPARAZIONE..." options={options.stato || []} icon="Activity" />
          </FormCard>

          {isEditing && (
            <div className="-mt-3 flex items-center px-1">
              <span className="text-[9px] font-black uppercase tracking-widest text-muted-foreground/40">
                {optionsLoading ? 'Caricamento valori esistenti...' : 'Scrivi liberamente o scegli un valore già usato'}
              </span>
            </div>
          )}

          <FormCard titolo="Identificazione" icon="Settings" gridClassName="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <ComboCampo label="N° Macchina" name="n_macchina" value={formData.n_macchina} isEditing={isEditing} onChange={handleInputChange} placeholder="Es. M-15" options={options.n_macchina || []} icon="Settings" />
            <ComboCampo label="Linea" name="linea" value={formData.linea} isEditing={isEditing} onChange={handleInputChange} placeholder="Es. LINEA 1" options={options.linea || []} icon="GitBranch" />
            <ComboCampo label="Colata" name="colata" value={formData.colata} isEditing={isEditing} onChange={handleInputChange} placeholder="Es. CCA" options={options.colata || []} icon="Layers" />
            <ComboCampo label="Posizione" name="posizione" value={formData.posizione} isEditing={isEditing} onChange={handleInputChange} placeholder="Es. Posizione 1" options={options.posizione || []} icon="MapPin" />
          </FormCard>

          <FormCard titolo="Numeri Seriali & RID" icon="Hash" descrizione="Confronto rapido S/N rientrato, smontato e montato" gridClassName="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <Campo label="S/N Rientrato" name="snrientrato" value={formData.snrientrato} isEditing={isEditing} onChange={handleInputChange} icon="Hash" />
            <Campo label="S/N Smontato" name="snsmontato" value={formData.snsmontato} isEditing={isEditing} onChange={handleInputChange} icon="Hash" />
            <Campo label="S/N Montato" name="snmontato" value={formData.snmontato} isEditing={isEditing} onChange={handleInputChange} icon="Hash" />
            <ComboCampo label="Stato RID" name="stato_rid" value={formData.stato_rid} isEditing={isEditing} onChange={handleInputChange} placeholder="Es. OK" options={options.stato_rid || []} icon="ShieldCheck" />
          </FormCard>

          <FormCard titolo="Causa Freno" icon="AlertTriangle" gridClassName="grid-cols-1">
            <CausaFrenoField value={Boolean(formData.causa_freno)} isEditing={isEditing} onChange={handleInputChange} />
          </FormCard>

          <FormCard titolo="Tempistiche" icon="Calendar" gridClassName="grid-cols-1 sm:grid-cols-2">
            <div className="flex flex-col gap-1.5 group">
              <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 group-focus-within:text-primary transition-colors">Data Intervento</span>
              {isEditing ? (
                <input type="datetime-local" name="data_intervento" value={toDatetimeLocal(formData.data_intervento)} onChange={handleInputChange} className="w-full rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all backdrop-blur-md bg-muted/30 border border-border/40 focus:border-primary/50 focus:bg-background/80 focus:ring-4 focus:ring-primary/10 hover:border-border/70" />
              ) : (
                <span className="font-mono text-[13px] font-bold text-foreground/85">{fmtDatetime(formData.data_intervento)}</span>
              )}
            </div>
            <div className="flex flex-col gap-1.5 group">
              <span className="text-[10px] font-black uppercase tracking-[.15em] text-muted-foreground/50 group-focus-within:text-primary transition-colors">Data Rientro</span>
              {isEditing ? (
                <input type="datetime-local" name="data_rientro" value={toDatetimeLocal(formData.data_rientro)} onChange={handleInputChange} className="w-full rounded-2xl px-4 py-3 text-sm font-bold outline-none transition-all backdrop-blur-md bg-muted/30 border border-border/40 focus:border-primary/50 focus:bg-background/80 focus:ring-4 focus:ring-primary/10 hover:border-border/70" />
              ) : (
                <span className="font-mono text-[13px] font-bold text-foreground/85">{fmtDatetime(formData.data_rientro)}</span>
              )}
            </div>
          </FormCard>

          <FormCard titolo="Note" icon="FileText" gridClassName="grid-cols-1">
            <Campo label="Note intervento" name="note" value={formData.note} isEditing={isEditing} onChange={handleInputChange} type="textarea" placeholder="Osservazioni..." icon="FileText" />
          </FormCard>

          <FormCard titolo="Metadati Sistema" icon="Database" gridClassName="grid-cols-1 sm:grid-cols-2">
            <Campo label="ID Record" value={item.id} mono />
            <Campo label="Creato il" value={fmtDatetime(item.created_at)} mono />
          </FormCard>
        </div>

        {/* Footer */}
        <div className="shrink-0 px-6 md:px-8 py-5 border-t border-border/40 bg-muted/10 backdrop-blur-sm flex items-center justify-between gap-3 relative z-10">
          <div className="hidden sm:flex items-center gap-2 text-muted-foreground/30">
            <Icon name="Fingerprint" size={12} />
            <span className="text-[10px] font-bold uppercase tracking-tighter truncate max-w-[220px]">RD_CCO | {formData.linea || 'N/D'} — {fmtDate(formData.data_intervento)}</span>
          </div>
          <div className="flex gap-2.5 w-full sm:w-auto">
            {isEditing && (
              <>
                <button onClick={() => setShowDeleteConfirm(true)} disabled={loading} className="px-5 py-3 bg-red-500/10 text-red-500 rounded-2xl text-[11px] font-black uppercase tracking-widest border border-red-500/20">Elimina</button>
                <button onClick={handleAnnulla} className="px-5 py-3 bg-muted text-foreground rounded-2xl text-[11px] font-black uppercase tracking-widest border border-border/40">Annulla</button>
              </>
            )}
            {!isEditing && (
              <button onClick={onClose} className="px-5 py-3 bg-muted text-foreground/70 rounded-2xl text-[11px] font-black uppercase tracking-widest border border-border/40">Chiudi</button>
            )}
            <button onClick={isEditing ? handleSave : handleEditClick} disabled={loading || isCheckingAuth} className="flex-1 sm:flex-none px-7 py-3 bg-primary text-primary-foreground rounded-2xl text-[11px] font-black uppercase tracking-[.2em] shadow-lg shadow-primary/20 disabled:opacity-50">
              {(loading || isCheckingAuth) ? 'Elaborazione...' : isEditing ? 'Salva Modifiche' : 'Modifica'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Detagli_Intervento;